import {
	StoryData,
	StoryProjectData,
	StoryClientData,
	StoryKeyMomentsData,
	StorySeoData,
	StoryFeedbackData,
	StoryDataValidated,
	StoryProjectDataValidated,
	StoryClientDataValidated,
	StorySeoDataValidated,
	StoryKeyMomentsDataValidated,
	StoryFeedbackDataValidated,
	StoryTechnologiesData,
	StoryTechnologiesDataValidated,
	StoryMeetingData,
	StoryMeetingDataValidated,
	StoryTeamMembersData,
	StoryTeamMembersDataValidated,
} from '@repo/database'

// Вспомогательные типы

// Обобщенные вспомогательные типы
type NonNullableDeep<T> = T extends object ? { [K in keyof T]: NonNullableDeep<T[K]> } : NonNullable<T>

// Обновленные типы для извлечения ключей
type TextFieldKeys<T> = {
	[K in keyof NonNullableDeep<T>]: NonNullableDeep<T>[K] extends string ? K : never
}[keyof NonNullableDeep<T>]

type ImageFieldKeys<T> = {
	[K in keyof NonNullableDeep<T>]: NonNullableDeep<T>[K] extends { url: string } ? K : never
}[keyof NonNullableDeep<T>]

// Функция для проверки текстовых полей
function validateTextFields<T>(section: NonNullable<T>, fields: TextFieldKeys<T>[]): boolean {
	return fields.every((field) => {
		const value = section[field as keyof NonNullable<T>]
		return typeof value === 'string' && value.trim() !== ''
	})
}

// Функция для проверки полей изображений
function validateImageFields<T>(section: NonNullable<T>, fields: ImageFieldKeys<T>[]): boolean {
	return fields.every((field) => {
		const image = section[field as keyof NonNullable<T>] as { url: string }
		return image !== null && typeof image.url === 'string' && image.url.startsWith('https://')
	})
}

function validateAtLeastOneTextField<T>(section: NonNullable<T>, fields: TextFieldKeys<T>[]): boolean {
	return fields.some((field) => {
		const value = section[field as keyof NonNullable<T>]
		return typeof value === 'string' && value.trim().length > 0
	})
}

// Обновленная функция валидации проекта
function validateProjectData(project: StoryProjectData): StoryProjectDataValidated {
	if (!project) return null

	const textFields: TextFieldKeys<StoryProjectData>[] = ['portfolioItemName', 'industry', 'projectLength', 'description', 'deepDescription']
	const imageFields: ImageFieldKeys<StoryProjectData>[] = ['storyImage']

	const isCompleted = validateTextFields(project, textFields) && validateImageFields(project, imageFields)

	return { ...project, isCompleted }
}

// Функция для валидации клиента
function validateKeyMoments(keyMoments: StoryKeyMomentsData): StoryKeyMomentsDataValidated {
	if (!keyMoments) return null

	const textFields: TextFieldKeys<StoryKeyMomentsData>[] = [
		'momentOfTrust',
		'introductionMoment',
		'dealClarityMoment',
		'roiMoment',
		'ribbonCuttingMoment',
		'eurekaMoment',
		'redMoment',
	]
	// const imageFields: ImageFieldKeys<StoryKeyMomentsData>[] = ['clientLogoImage']

	const isCompleted = validateAtLeastOneTextField(keyMoments, textFields)

	return { ...keyMoments, isCompleted }
}
function validateClientData(client: StoryClientData): StoryClientDataValidated {
	if (!client) return null

	const textFields: TextFieldKeys<StoryClientData>[] = ['clientName', 'aboutClient', 'whyNow']
	const imageFields: ImageFieldKeys<StoryClientData>[] = ['clientLogoImage']

	const isCompleted = validateTextFields(client, textFields) && validateImageFields(client, imageFields)

	return { ...client, isCompleted }
}

// Функция для валидации SEO
function validateSeoData(seo: StorySeoData): StorySeoDataValidated {
	if (!seo) return null

	const textFields: TextFieldKeys<StorySeoData>[] = ['metaName', 'metaDescription']

	const isCompleted = validateTextFields(seo, textFields)

	return { ...seo, isCompleted }
}

function validateFeedbackData(feedback: StoryFeedbackData): StoryFeedbackDataValidated {
	if (!feedback) return null

	const textFields: TextFieldKeys<StoryFeedbackData>[] = ['firstName', 'lastName', 'title', 'testimonial']

	// либо все поля пустые, либо все не пустые. Не может быть только часть полей.
	const isCompleted =
		textFields.every((field) => {
			const value = feedback[field]
			return typeof value === 'string' && value.trim() === ''
		}) ||
		textFields.every((field) => {
			const value = feedback[field]
			return typeof value === 'string' && value.trim() !== ''
		})

	return { ...feedback, isCompleted }
}

function validateStoryTechnologiesData(storyTechnologies: StoryTechnologiesData): StoryTechnologiesDataValidated {
	// console.log('storyTechnologies', storyTechnologies)
	if (!storyTechnologies) return null
	if (storyTechnologies.length === 0) return { data: [], isCompleted: false }
	return { data: storyTechnologies, isCompleted: true }
}

function validateStoryMeetingData(meeting: StoryMeetingData): StoryMeetingDataValidated {
	if (!meeting) return null
	const textFields: TextFieldKeys<StoryMeetingData>[] = ['meetingContact', 'calendarLink', 'modalDescription', 'bannerDescription']
	const isCompleted =
		textFields.every((field) => {
			const value = meeting[field]
			return typeof value === 'string' && value.trim() === ''
		}) ||
		textFields.every((field) => {
			const value = meeting[field]
			return typeof value === 'string' && value.trim() !== ''
		})
	// const textFields: TextFieldKeys<StoryMeetingData>[] = ['meetingContact', 'calendarLink', 'modalDescription', 'bannerDescription']
	// const isCompleted = validateTextFields(meeting, textFields)
	return { ...meeting, isCompleted }
}

function validateTeamMembersData(teamMembers: StoryTeamMembersData): StoryTeamMembersDataValidated {
	if (!teamMembers) return null
	if (teamMembers.length === 0) return { data: [], isCompleted: false }
	return { data: teamMembers, isCompleted: true }
}

// Главная функция для валидации всей StoryData
export function validateStoryData(storyData: StoryData): StoryDataValidated {
	const validatedProject = validateProjectData(storyData.project)
	const validatedClient = validateClientData(storyData.client)
	const validatedSeo = validateSeoData(storyData.seo)
	const validatedKeyMoments = validateKeyMoments(storyData.keyMoments)
	const validatedFeedback = validateFeedbackData(storyData.feedback)
	const validatedStoryTechnologies = validateStoryTechnologiesData(storyData.technologies)
	const validatedTeamMembers = validateTeamMembersData(storyData.teamMembers)
	const validatedStoryMeeting = validateStoryMeetingData(storyData.meeting)

	return {
		...storyData,
		project: validatedProject,
		client: validatedClient,
		seo: validatedSeo,
		keyMoments: validatedKeyMoments,
		feedback: validatedFeedback,
		technologies: validatedStoryTechnologies,
		meeting: validatedStoryMeeting,
		teamMembers: validatedTeamMembers,
	}
}
